import React from 'react';
import { useTranslation } from 'react-i18next';

import { isEmailValid } from '@/utils/strings';
import {
  COMMERCE_GATE_PROVIDER,
  GATE2WAY_PROVIDER,
  STRIPE_PROVIDER,
  isInstructionRail,
  type CheckoutProvider,
} from '@/utils/session-invoice';
import { useThemeMode } from '@/nova/theme/OwsTheme';
import { PREVIEW_LOADER_SRC } from '../../../../components/wizard/PreviewPlaceholder';
import { formatAmountWithCode } from '../../../../data/billing';
import { CFG } from '../../../../../../theme/configurator-tokens';
import { FINISH } from '../../finish.tokens';
import { CardBrandMarks, StripeBadge } from './BrandMarks';
import { PixLogo } from './PixLogo';
import { SpeiLogo } from './SpeiLogo';
import type { CardPhase } from './useCheckout';
import {
  Root,
  Scroll,
  SectionLabel,
  SectionLabelTop,
  Field,
  FieldLabel,
  Input,
  FieldError,
  FieldRow,
  FieldControl,
  LockTick,
  ConfirmButton,
  ConfirmLabel,
  Methods,
  TabStrip,
  Tab,
  TabMark,
  TabLabel,
  TabPanel,
  TabPanelBadge,
  Option,
  OptionHead,
  RadioInput,
  RadioDot,
  OptionLabels,
  OptionTitle,
  OptionSub,
  OptionMark,
  OptionBody,
  OptionIntro,
  IntroSentence,
  CardStatusBox,
  Spinner,
  CardStatusText,
  RetryButton,
  RetryLabel,
  ErrorAlert,
  ErrorAlertIcon,
  ErrorAlertText,
  Footer,
  TotalRow,
  TotalLabels,
  TotalLabel,
  TotalSub,
  TotalAmount,
  SubmitButton,
  SubmitLabel,
  CtaSpinner,
} from './PaymentMethodForm.styles';

export type PaymentMethodFormProps = {
  /** Rails to offer, in display order. One entry renders a single (still selected) option. */
  methods: CheckoutProvider[];
  method: CheckoutProvider;
  onMethod: (method: CheckoutProvider) => void;
  /** Contact email — global, shared by both rails. */
  email: string;
  onEmail: (value: string) => void;
  /** Instruction rails only (SPEI, Pix): the name the payment will be registered under. */
  fullName: string;
  onFullName: (value: string) => void;
  /** The payer name has been committed: the field locks and the footer CTA opens up. */
  nameConfirmed: boolean;
  /** Commit the payer name. Only called once it passes validation. */
  onConfirmName: () => void;
  /** Reopen the payer name for editing. */
  onEditName: () => void;
  /** Amount due today, for the footer total. */
  amount: number;
  currencySymbol: string;
  currencyCode: string;
  totalLabel: string;
  totalSub?: string;
  /** True while the selected rail's submit is in flight. */
  submitting: boolean;
  /** Request-level failure, shown as a banner above the footer. */
  error?: string | null;
  /** Card rail state, so the option can show its own loading / retry. */
  cardPhase: CardPhase;
  cardError?: string | null;
  onCardRetry: () => void;
  /** Accept voiding the bank coordinates on screen and start the card rail. */
  onCardConfirmSupersede: () => void;
  /**
   * The instruction rail whose details the card would void (what `confirm-supersede` warns about).
   * `gate2way` words the warning for a Pix code; anything else keeps the bank transfer sentence.
   */
  supersedes?: 'commerce-gate' | 'gate2way' | null;
  /** The Stripe Elements payment form, rendered inside the card option once it is chargeable. */
  cardSlot?: React.ReactNode;
  /**
   * Rendered in the mobile sheet, where the screen is the checkout: the section headings and the
   * intro's scene-setting sentence are dropped, because the sheet's own title already says what
   * this is and vertical space is the scarce thing.
   *
   * Passed down from the flow's ONE mobile decision (`useMediaQuery(CFG_MOBILE)` picks the mobile
   * app) rather than re-tested as a CSS breakpoint here, so the two can never disagree.
   */
  compact?: boolean;
  /**
   * Fired once the selected rail's own fields pass validation. The card rail supplies its own
   * handler (it has to confirm with Stripe); the bank-transfer rail creates the invoice.
   */
  onSubmit: (email: string, fullName: string) => void;
};

/** Warning glyph (rounded triangle + exclamation) — stroke from the error token. */
const WarningGlyph = (): React.ReactElement => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 3.6 21.2 19.5a1.4 1.4 0 0 1-1.2 2.1H4a1.4 1.4 0 0 1-1.2-2.1L12 3.6Z"
      stroke={FINISH.errorIconStroke}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M12 9.5v4.2" stroke={FINISH.errorIconStroke} strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="17.2" r="1.05" fill={FINISH.errorIconStroke} />
  </svg>
);

/** Confirmed tick, in the same success tone the copy-to-clipboard buttons flash. */
const TickGlyph = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="m5 12.5 4.5 4.5L19 7.5"
      stroke={CFG.success}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * A SPEI transfer must be registered against the payer's name, and the backend enforces it
 * (`/session/invoice` rejects a `commerce-gate` call without `full_name`). Two words is a
 * weak-but-honest signal that we got a name rather than a nickname; anything stricter would
 * reject legitimate Mexican names.
 */
/** Analytics id of a rail's radio. */
const methodGaId = (option: CheckoutProvider): string => {
  switch (option) {
    case COMMERCE_GATE_PROVIDER:
      return 'cfg-mx-method-spei';
    case GATE2WAY_PROVIDER:
      return 'cfg-mx-method-pix';
    case STRIPE_PROVIDER:
      return 'cfg-mx-method-card';
  }
};

const isFullNameValid = (value: string): boolean => value.trim().split(/\s+/).filter(Boolean).length >= 2;

/**
 * The checkout's first screen: who to send the confirmation to, and how to pay.
 *
 * The rails are a radio group of accordion cards: exactly one is selected, and only the selected
 * one shows its fields. There are three rails (SPEI bank transfer, Pix, card); a market offers at
 * most one instruction rail beside the card. One open rail keeps them from competing: the buyer
 * never sees a card form and a payer-name field at the same time and has to work out which one
 * the button belongs to. Selecting the card collapses the payer-name field, and vice versa.
 *
 * The email sits ABOVE the group, deliberately outside it. There is one order and one buyer, so
 * asking twice (or losing the address on a rail switch) would be a bug, not a feature — and the
 * invoice is issued against the address on the session either way.
 *
 * Validation runs on submit and then live per field, so the buyer isn't scolded for a field they
 * haven't finished typing yet. Only the SELECTED rail's fields are validated: a collapsed rail's
 * requirements are not the buyer's problem.
 *
 * Purely presentational — every decision (which rails exist, whether the card is chargeable, what
 * submitting does) arrives as a prop, so it renders standalone in a component test.
 */
export const PaymentMethodForm = ({
  methods,
  method,
  onMethod,
  email,
  onEmail,
  fullName,
  onFullName,
  nameConfirmed,
  onConfirmName,
  onEditName,
  amount,
  currencySymbol,
  currencyCode,
  totalLabel,
  totalSub,
  submitting,
  error,
  cardPhase,
  cardError,
  onCardRetry,
  onCardConfirmSupersede,
  supersedes,
  cardSlot,
  compact,
  onSubmit,
}: PaymentMethodFormProps): React.ReactElement => {
  const { t } = useTranslation();
  // Field errors stay hidden until the first submit attempt, then track the value live.
  const [submitted, setSubmitted] = React.useState(false);
  // A rejected Confirm reveals the NAME error on its own, without also scolding the buyer about
  // an email they may not have reached yet.
  const [nameAttempted, setNameAttempted] = React.useState(false);
  // Studio theme: the loader art is white on the dark pane, darkened on the light one.
  const onLight = useThemeMode() === 'light';

  const onCard = method === STRIPE_PROVIDER;
  const emailBad = !isEmailValid(email);
  // Only the bank transfer needs a payer name, so it is the only rail that can fail on one.
  const nameBad = !onCard && !isFullNameValid(fullName);
  const nameErrorShown = (submitted || nameAttempted) && nameBad;
  // The card rail's button waits for Stripe to be mountable; the transfer's waits for the payer
  // name to be confirmed — which is the point of the Confirm button: once it is pressed, the
  // footer CTA is the only thing left to do.
  const notReady = onCard ? cardPhase !== 'ready' : !nameConfirmed;
  /**
   * The CTA is WAITING ON US, as opposed to merely being held back.
   *
   * Two causes, both invisible to the buyer: the invoice call that fetches the bank coordinates,
   * and Stripe becoming chargeable. An unconfirmed payer name is NOT one of them — nothing is in
   * flight there, the buyer simply has something left to do, and spinning at them would suggest
   * otherwise.
   */
  const ctaBusy = submitting || (onCard && cardPhase === 'preparing');

  /** Commit the payer name, or surface why it can't be committed. */
  const confirmName = (): void => {
    setNameAttempted(true);
    if (nameBad) return;
    onConfirmName();
  };

  /**
   * The selected rail's fields. Hoisted so the stacked (desktop) and segmented (mobile) layouts
   * render exactly the same thing — the two differ in how the CHOICE is presented, never in what
   * the chosen rail then asks for.
   */
  const railBody = (option: CheckoutProvider): React.ReactNode => {
    if (isInstructionRail(option)) {
      // Written out per rail: the i18n extractor only harvests LITERAL `text` props.
      const intro =
        option === GATE2WAY_PROVIDER ? (
          <>
            {compact ? null : (
              <>
                <IntroSentence
                  text="We'll show you a Pix QR code and a Pix Copia e Cola code on the next screen."
                  tag="span"
                />{' '}
              </>
            )}
            <IntroSentence text="Tell us the name the payment will come from." tag="span" />
          </>
        ) : (
          <>
            {compact ? null : (
              <>
                <IntroSentence
                  text="We'll give you the account details for the transfer on the next screen."
                  tag="span"
                />{' '}
              </>
            )}
            <IntroSentence text="Tell us the name the transfer will come from." tag="span" />
          </>
        );
      return (
        <>
          <OptionIntro>{intro}</OptionIntro>
          <Field>
            <FieldLabel text="Full name" tag="span" />
            <FieldRow>
              <FieldControl>
                <Input
                  type="text"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => onFullName(e.target.value)}
                  // Enter confirms rather than submitting the form. The CTA is gated on the
                  // confirmation, so submitting from here would only hit a disabled button and
                  // look like the key did nothing.
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter') return;
                    e.preventDefault();
                    confirmName();
                  }}
                  readOnly={nameConfirmed}
                  $locked={nameConfirmed}
                  placeholder={t('Name on the bank account')}
                  aria-label={t('Full name')}
                  aria-invalid={nameErrorShown}
                />
                {nameConfirmed ? (
                  <LockTick>
                    <TickGlyph />
                  </LockTick>
                ) : null}
              </FieldControl>
              <ConfirmButton
                type="button"
                $ghost={nameConfirmed}
                data-ga-id={nameConfirmed ? 'cfg-mx-name-edit' : 'cfg-mx-name-confirm'}
                onClick={nameConfirmed ? onEditName : confirmName}
              >
                {nameConfirmed ? (
                  <ConfirmLabel text="Edit" tag="span" $ghost />
                ) : (
                  <ConfirmLabel text="Confirm" tag="span" />
                )}
              </ConfirmButton>
            </FieldRow>
            {nameErrorShown ? (
              <FieldError text="Enter your first and last name, as they appear on your bank account." tag="span" />
            ) : null}
          </Field>
        </>
      );
    }
    if (cardPhase === 'confirm-supersede') {
      // The buyer already has bank coordinates and may be mid-transfer against them. Starting the
      // card rail cancels that invoice, so it is their call, not a side effect of a radio click.
      return (
        <CardStatusBox>
          {supersedes === GATE2WAY_PROVIDER ? (
            <CardStatusText
              text="Paying by card replaces your Pix code. Do not pay the Pix code after switching, it would not count towards this order."
              tag="paragraph"
            />
          ) : (
            <CardStatusText
              text="Paying by card cancels the bank transfer details you were given. Any transfer you have already made would not count towards this order."
              tag="paragraph"
            />
          )}
          <RetryButton type="button" data-ga-id="cfg-mx-card-confirm-supersede" onClick={onCardConfirmSupersede}>
            <RetryLabel text="Pay by card instead" tag="span" />
          </RetryButton>
        </CardStatusBox>
      );
    }
    if (cardPhase === 'error') {
      return (
        <CardStatusBox>
          <CardStatusText
            text={cardError ?? t("We couldn't start the card payment. Check your connection and try again.")}
            tag="paragraph"
          />
          <RetryButton type="button" data-ga-id="cfg-mx-card-retry" onClick={onCardRetry}>
            <RetryLabel text="Try again" tag="span" />
          </RetryButton>
        </CardStatusBox>
      );
    }
    if (cardPhase === 'ready' && cardSlot) return cardSlot;
    return (
      <CardStatusBox>
        <Spinner $onLight={onLight} src={PREVIEW_LOADER_SRC} alt="" />
        <CardStatusText text="Starting secure checkout…" tag="paragraph" />
      </CardStatusBox>
    );
  };

  /**
   * A rail's label and mark, written out per rail rather than picked from a table: the i18n
   * extractor only harvests LITERAL `text` props. The segment labels are shortened, since the
   * full titles do not fit two-up and would wrap.
   */
  const tabContent = (option: CheckoutProvider, selected: boolean): React.ReactNode => {
    switch (option) {
      case COMMERCE_GATE_PROVIDER:
        return (
          <>
            <TabMark>
              <SpeiLogo height={18} />
            </TabMark>
            <TabLabel text="Bank transfer" tag="span" $selected={selected} />
          </>
        );
      case GATE2WAY_PROVIDER:
        return (
          <>
            <TabMark>
              <PixLogo height={18} />
            </TabMark>
            <TabLabel text="Pix" tag="span" $selected={selected} />
          </>
        );
      case STRIPE_PROVIDER:
        return (
          <>
            <TabMark>
              <CardBrandMarks />
            </TabMark>
            <TabLabel text="Card" tag="span" $selected={selected} />
          </>
        );
    }
  };

  /** The stacked layout's option head: title, subline and mark per rail. */
  const optionHeadContent = (option: CheckoutProvider): React.ReactNode => {
    switch (option) {
      case COMMERCE_GATE_PROVIDER:
        return (
          <>
            <OptionLabels>
              <OptionTitle text="Bank transfer" tag="span" />
              <OptionSub text="Pay from your banking app" tag="span" />
            </OptionLabels>
            <OptionMark>
              <SpeiLogo height={20} />
            </OptionMark>
          </>
        );
      case GATE2WAY_PROVIDER:
        return (
          <>
            <OptionLabels>
              <OptionTitle text="Pix" tag="span" />
              <OptionSub text="Scan or copy the code in your bank app" tag="span" />
            </OptionLabels>
            <OptionMark>
              <PixLogo height={20} />
            </OptionMark>
          </>
        );
      case STRIPE_PROVIDER:
        return (
          <>
            <OptionLabels>
              <OptionTitle text="Debit or credit card" tag="span" />
              {/* The badge says "processed by Stripe" better than a sentence can,
                  and is the form Stripe's own brand guidance asks for. */}
              <StripeBadge />
            </OptionLabels>
            <OptionMark>
              <CardBrandMarks />
            </OptionMark>
          </>
        );
    }
  };

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    setSubmitted(true);
    // The button is disabled while submitting, but a form can also be submitted with Enter,
    // so the handler self-gates rather than relying on the disabled attribute.
    if (submitting || notReady || emailBad || nameBad) return;
    onSubmit(email.trim(), fullName.trim());
  };

  const submitLabel = submitting
    ? onCard
      ? t('Processing…')
      : t('Getting your details…')
    : onCard
      ? t('Pay for your brand')
      : t('Continue');

  return (
    <Root onSubmit={submit} noValidate>
      <Scroll>
        {compact ? null : <SectionLabel text="Contact info" tag="paragraph" />}
        <Field>
          <FieldLabel text="Email address" tag="span" />
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            placeholder={t('you@email.com')}
            aria-label={t('Email address')}
            aria-invalid={submitted && emailBad}
          />
          {submitted && emailBad ? <FieldError text="Enter a valid email address." tag="span" /> : null}
        </Field>

        {compact ? null : <SectionLabelTop text="Choose payment method" tag="paragraph" />}
        {compact ? (
          <>
            {/* Still a radiogroup of real radios — only the arrangement changes. See TabStrip. */}
            <TabStrip role="radiogroup" aria-label={t('Payment method')}>
              {methods.map((option) => {
                const selected = option === method;
                return (
                  <Tab key={option} $selected={selected}>
                    <RadioInput
                      type="radio"
                      name="cfg-mx-payment-method"
                      value={option}
                      checked={selected}
                      onChange={() => onMethod(option)}
                      data-ga-id={methodGaId(option)}
                    />
                    {tabContent(option, selected)}
                  </Tab>
                );
              })}
            </TabStrip>
            <TabPanel>
              {onCard ? (
                <TabPanelBadge>
                  <StripeBadge />
                </TabPanelBadge>
              ) : null}
              {railBody(method)}
            </TabPanel>
          </>
        ) : (
          <Methods role="radiogroup" aria-label={t('Payment method')} $compact={compact}>
            {methods.map((option) => {
              const selected = option === method;
              return (
                <Option key={option} $selected={selected}>
                  <OptionHead>
                    <RadioInput
                      type="radio"
                      name="cfg-mx-payment-method"
                      value={option}
                      checked={selected}
                      onChange={() => onMethod(option)}
                      data-ga-id={methodGaId(option)}
                    />
                    <RadioDot $selected={selected} aria-hidden />
                    {optionHeadContent(option)}
                  </OptionHead>

                  {/* Rendered only for the selected rail: a collapsed option's inputs must be out
                      of the tab order and out of the accessibility tree, not merely invisible. */}
                  {selected ? <OptionBody>{railBody(option)}</OptionBody> : null}
                </Option>
              );
            })}
          </Methods>
        )}

        {error ? (
          <ErrorAlert role="alert">
            <ErrorAlertIcon>
              <WarningGlyph />
            </ErrorAlertIcon>
            <ErrorAlertText text={error} tag="paragraph" />
          </ErrorAlert>
        ) : null}
      </Scroll>

      <Footer>
        <TotalRow>
          <TotalLabels>
            <TotalLabel text={totalLabel} tag="span" />
            {totalSub ? <TotalSub text={totalSub} tag="span" /> : null}
          </TotalLabels>
          <TotalAmount
            text="{{symbol}}{{amount}}"
            tag="span"
            placeholders={{ symbol: currencySymbol, amount: formatAmountWithCode(amount, currencyCode) }}
          />
        </TotalRow>
        <SubmitButton
          type="submit"
          data-ga-id={
            onCard ? 'cfg-mx-card-pay' : method === GATE2WAY_PROVIDER ? 'cfg-mx-pix-continue' : 'cfg-mx-spei-continue'
          }
          disabled={submitting || notReady}
        >
          {ctaBusy ? <CtaSpinner aria-hidden /> : null}
          <SubmitLabel text={submitLabel} tag="span" />
        </SubmitButton>
      </Footer>
    </Root>
  );
};

export default PaymentMethodForm;
